import { api, ApiError } from "./api";
import { store } from "./store";

let socket: WebSocket | null = null;
let retry = 500;
let stopped = false;
let timer: ReturnType<typeof setTimeout> | undefined;
let booting: Promise<void> | null = null;

/** Loads the snapshot and connects. Safe to call twice (React's strict mode does): the second call joins the first. */
export function boot(): Promise<void> {
  booting ??= start().finally(() => (booting = null));
  return booting;
}

async function start() {
  stopped = false;
  store.setStatus("loading");
  try {
    const { signedIn } = await api.get<{ signedIn: boolean }>("/auth/session");
    if (!signedIn) return store.setStatus("signed-out");
    const snap = await api.snapshot();
    store.load(snap);
    connect();
    // Recent history for activity feeds. Older events are ignored for state, so this is safe.
    void api.eventsSince(Math.max(0, snap.seq - 400)).then((r) => store.applyEvents(r.events), () => {});
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) return;
    store.setStatus("error", e instanceof Error ? e.message : "Could not load.");
  }
}

export function stop() {
  stopped = true;
  clearTimeout(timer);
  socket?.close();
  socket = null;
}

/** Access changed (a new project, a new role): load everything again. */
async function reload() {
  try {
    store.load(await api.snapshot());
  } catch {
    // The socket's own reconnect will try again.
  }
}

/** Catch up after the server says it may have missed events, or after we were away. */
async function catchUp() {
  try {
    const { events, truncated } = await api.eventsSince(Math.max(0, store.seq - 200));
    if (truncated) store.load(await api.snapshot());
    else store.applyEvents(events);
  } catch {
    // The socket's own reconnect will try again.
  }
}

function connect() {
  if (stopped) return;
  if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) return;
  store.setConnection("connecting");
  const ws = new WebSocket(`${location.protocol === "https:" ? "wss" : "ws"}://${location.host}/ws`);
  socket = ws;
  ws.onopen = () => {
    retry = 500;
    ws.send(JSON.stringify({ type: "hello", since: store.seq }));
    store.setConnection("live");
  };
  ws.onmessage = (m) => {
    const msg = JSON.parse(String(m.data)) as { type: string; events?: never; actors?: string[] };
    if (msg.type === "events" && msg.events) store.applyEvents(msg.events);
    else if (msg.type === "presence" && msg.actors) store.setPresence(msg.actors);
    else if (msg.type === "resync") {
      if ((msg as { full?: boolean }).full) void reload();
      else void catchUp();
    }
  };
  ws.onclose = () => {
    if (socket !== ws) return;
    socket = null;
    store.setConnection("offline");
    store.setPresence([]);
    if (stopped) return;
    timer = setTimeout(connect, retry);
    retry = Math.min(retry * 2, 15_000);
  };
}

// A laptop waking from sleep may hold a dead socket; check as soon as the tab is visible again.
if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState !== "visible" || stopped || store.status !== "ready") return;
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      clearTimeout(timer);
      retry = 500;
      connect();
    } else {
      void catchUp();
    }
  });
}
